import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Download, ExternalLink, Factory, Package, Pencil, Plus, Search, ShieldCheck, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const FILTERS = [
  { key: "active", label: "Attivi" },
  { key: "all", label: "Tutti" },
  { key: "archived", label: "Archiviati" },
  { key: "geecom", label: "Importati da Geecom" },
  { key: "imported", label: "Importati dal sito" },
  { key: "manual", label: "Inseriti manualmente" },
  { key: "no_manufacturer", label: "Produttore da assegnare" },
  { key: "with_manufacturer", label: "Con produttore" },
  { key: "not_detected", label: "Non rilevati nell'ultima scansione" },
];

export default function Products() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("administrator");

  const [items, setItems] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("active");
  const [manuFilter, setManuFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 50;

  const [dlg, setDlg] = useState(false);
  const [detail, setDetail] = useState(null); // product for detail dialog
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ product_code: "", product_title: "", manufacturer_id: "", manufacturer_name: "", description: "", warranty_months: 24, active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/products", { params: { filter, manufacturer_id: manuFilter || undefined, category_id: categoryFilter || undefined, q: q || undefined, limit: PAGE_SIZE, skip: 0 } });
      setItems(r.data);
      setTotal(Number(r.headers["x-total-count"] || r.data.length));
      setHasMore((r.headers["x-has-more"] || "false") === "true");
    } catch (e) { toast.error("Errore caricamento prodotti"); }
    finally { setLoading(false); }
  }, [filter, manuFilter, categoryFilter, q]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/products", { params: { filter, manufacturer_id: manuFilter || undefined, category_id: categoryFilter || undefined, q: q || undefined, limit: PAGE_SIZE, skip: items.length } });
      setItems((prev) => [...prev, ...r.data]);
      setHasMore((r.headers["x-has-more"] || "false") === "true");
    } catch (e) { toast.error("Errore caricamento prodotti"); }
    finally { setLoading(false); }
  }, [filter, manuFilter, categoryFilter, q, items.length]);

  // Debounced server-side search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [filter, manuFilter, categoryFilter, q]);

  useEffect(() => {
    api.get("/manufacturers").then((r) => setManufacturers(r.data)).catch(() => {});
    api.get("/product-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Server-side search + pagination; no client-side filtering.
  const filtered = items;

  const openDetail = async (id) => {
    const r = await api.get(`/products/${id}`);
    setDetail(r.data); setEditing(false);
    setForm({
      product_code: r.data.product_code || "",
      product_title: r.data.product_title || r.data.model || "",
      manufacturer_id: r.data.manufacturer_id || "",
      manufacturer_name: r.data.manufacturer_name || "",
      description: r.data.description || "",
      warranty_months: r.data.warranty_months || 24,
      active: r.data.active !== false,
    });
  };

  const create = async () => {
    if (!form.product_code.trim()) return toast.error("Codice prodotto obbligatorio");
    if (!form.product_title.trim()) return toast.error("Titolo prodotto obbligatorio");
    try {
      await api.post("/products", form);
      toast.success("Prodotto creato.");
      setDlg(false);
      setForm({ product_code: "", product_title: "", manufacturer_id: "", manufacturer_name: "", description: "", warranty_months: 24, active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveEdit = async () => {
    if (!detail) return;
    try {
      const r = await api.patch(`/products/${detail.id}`, form);
      setDetail(r.data);
      setEditing(false);
      toast.success("Prodotto aggiornato.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const archive = async () => {
    if (!detail || !window.confirm("Archiviare il prodotto? Lo storico verrà conservato.")) return;
    await api.post(`/products/${detail.id}/archive`);
    toast.success("Prodotto archiviato.");
    openDetail(detail.id); load();
  };
  const reactivate = async () => {
    if (!detail) return;
    await api.post(`/products/${detail.id}/reactivate`);
    toast.success("Prodotto riattivato.");
    openDetail(detail.id); load();
  };

  const setManu = (id) => {
    const m = manufacturers.find((x) => x.id === id);
    setForm({ ...form, manufacturer_id: id, manufacturer_name: m ? m.name : "" });
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">Catalogo prodotti</h1>
          <p className="text-slate-500">Anagrafica prodotti distribuiti da AMA Sicurezza. Importa dal sito o da file.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="products-search" className="pl-9 w-64" placeholder="Codice, titolo, marchio…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {isAdmin && (
            <>
              <Link to="/catalogo/import-geecom"><Button data-testid="btn-import-geecom" className="ama-btn-primary rounded-full"><Upload className="w-4 h-4 mr-1" /> Importa catalogo Geecom</Button></Link>
              <Link to="/catalogo/import-sito"><Button data-testid="btn-import-website" variant="outline" className="rounded-full"><Download className="w-4 h-4 mr-1" /> Importa dal sito</Button></Link>
              <Link to="/catalogo/import-file"><Button data-testid="btn-import-file" variant="outline" className="rounded-full">Import CSV generico</Button></Link>
              <Link to="/catalogo/assegnazione-produttore"><Button data-testid="btn-bulk-manu" variant="outline" className="rounded-full"><Factory className="w-4 h-4 mr-1" /> Assegna produttore</Button></Link>
              <Link to="/catalogo/diagnostica"><Button data-testid="btn-diagnostica" variant="outline" className="rounded-full"><ShieldCheck className="w-4 h-4 mr-1" /> Diagnostica</Button></Link>
              <Link to="/catalogo/storico"><Button data-testid="btn-history" variant="outline" className="rounded-full">Storico</Button></Link>
              <Button data-testid="product-add-btn" onClick={() => setDlg(true)} className="ama-btn-primary rounded-full"><Plus className="w-4 h-4 mr-1" /> Nuovo</Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="product-filter-bar">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`product-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.key ? "bg-[#0a0a2e] text-white border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>{f.label}</button>
        ))}
        <select data-testid="product-manufacturer-filter" value={manuFilter} onChange={(e) => setManuFilter(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-1.5 bg-white text-slate-700">
          <option value="">Tutti i marchi</option>
          {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select data-testid="product-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-1.5 bg-white text-slate-700">
          <option value="">Tutte le categorie</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading && <div className="text-center text-slate-500 py-6">Caricamento…</div>}
      <div className="grid gap-2">
        {filtered.map((p) => {
          const isArchived = p.active === false;
          const isImported = p.source_type === "website";
          const notDetected = p.website_detection_status === "not_detected";
          return (
            <div key={p.id} className={`ama-card p-3 flex items-center gap-3 cursor-pointer hover:border-[#d63384] transition ${isArchived ? "opacity-60" : ""}`}
              data-testid={`product-row-${p.product_code}`} onClick={() => openDetail(p.id)}>
              <div className="w-10 h-10 rounded-lg bg-fuchsia-50 flex items-center justify-center text-[#d63384]"><Package className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-sm font-bold text-[#0a0a2e]">{p.product_code}</div>
                  <div className="font-semibold text-[#0a0a2e] truncate">{p.product_title || p.model || "-"}</div>
                  {isArchived && <Badge variant="outline" className="text-[10px]">Archiviato</Badge>}
                  {isImported && <Badge variant="outline" className="text-[10px]">Importato</Badge>}
                  {notDetected && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Non rilevato</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1 flex-wrap">
                  <Factory className="w-3 h-3" /> {p.manufacturer_name || <span className="italic">Produttore da assegnare</span>}
                  {(p.category_name || p.source_category_name) && (<> · <span className="text-slate-600 font-medium">{p.category_name || p.source_category_name}</span></>)}
                  {p.source_product_url && (<> · <a href={p.source_product_url} target="_blank" rel="noopener noreferrer" className="text-[#d63384] hover:underline inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>Sito AMA <ExternalLink className="w-3 h-3" /></a></>)}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && <div className="ama-card p-8 text-center text-slate-500">Nessun prodotto trovato.</div>}
        {hasMore && (
          <button
            type="button"
            data-testid="products-load-more"
            onClick={loadMore}
            disabled={loading}
            className="mt-2 self-center rounded-full border border-slate-200 bg-white hover:border-[#d63384] px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {loading ? "Caricamento…" : `Carica altri 50 prodotti (${items.length} di ${total})`}
          </button>
        )}
        {!loading && !hasMore && items.length > 0 && (
          <div className="text-xs text-slate-400 text-center py-2">Fine risultati · {items.length} di {total}</div>
        )}
      </div>

      {/* New product dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuovo prodotto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label>Codice prodotto *</Label><Input data-testid="new-code" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} /></div>
            <div><Label>Titolo prodotto *</Label><Input data-testid="new-title" value={form.product_title} onChange={(e) => setForm({ ...form, product_title: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Marchio</Label>
              <select data-testid="new-manufacturer" value={form.manufacturer_id} onChange={(e) => setManu(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="">— Seleziona —</option>
                {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Garanzia (mesi)</Label><Input type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: +e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button data-testid="new-save" onClick={create} className="ama-btn-primary">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{detail.product_code}</span>
                  <span className="truncate">{detail.product_title || detail.model}</span>
                  {detail.active === false && <Badge variant="outline">Archiviato</Badge>}
                </DialogTitle>
              </DialogHeader>
              {!editing ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <KV k="Codice" v={detail.product_code} />
                  <KV k="Titolo" v={detail.product_title || detail.model} />
                  <KV k="Marchio" v={detail.manufacturer_name || "Da assegnare"} />
                  <KV k="Categoria" v={detail.category_name || detail.source_category_name || "—"} />
                  <KV k="Marchio rilevato dal sito" v={detail.source_brand_name || "—"} />
                  <KV k="Origine" v={detail.source_type === "website" ? "Sito AMA" : "Manuale"} />
                  {detail.source_product_url && <div className="col-span-2 text-xs"><span className="text-slate-500 uppercase tracking-wider">Pagina prodotto sul sito AMA</span><br /><a href={detail.source_product_url} target="_blank" rel="noopener noreferrer" className="text-[#d63384] hover:underline inline-flex items-center gap-1 font-mono break-all">{detail.source_product_url} <ExternalLink className="w-3 h-3" /></a></div>}
                  <KV k="Importato il" v={detail.imported_at ? new Date(detail.imported_at).toLocaleString("it-IT") : "—"} />
                  <KV k="Ultima sincronizzazione" v={detail.last_synced_at ? new Date(detail.last_synced_at).toLocaleString("it-IT") : "—"} />
                  <KV k="Rilevato dal sito" v={detail.website_detection_status === "detected" ? "Sì" : (detail.website_detection_status === "not_detected" ? "Non rilevato nell'ultima scansione" : "—")} />
                  <KV k="Stato" v={detail.active === false ? "Archiviato" : "Attivo"} />
                  {detail.description && <div className="col-span-2"><KV k="Descrizione" v={detail.description} /></div>}
                  {isAdmin && (
                    <div className="col-span-2 flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                      <Button data-testid="detail-edit-product" variant="outline" onClick={() => setEditing(true)}><Pencil className="w-4 h-4 mr-1" /> Modifica prodotto</Button>
                      {detail.active !== false ? (
                        <Button data-testid="detail-archive-product" variant="outline" onClick={archive}><Archive className="w-4 h-4 mr-1" /> Archivia prodotto</Button>
                      ) : (
                        <Button data-testid="detail-reactivate-product" variant="outline" onClick={reactivate}><ArchiveRestore className="w-4 h-4 mr-1" /> Riattiva prodotto</Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label>Codice prodotto *</Label><Input value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} /></div>
                  <div><Label>Titolo prodotto *</Label><Input value={form.product_title} onChange={(e) => setForm({ ...form, product_title: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Marchio</Label>
                    <select value={form.manufacturer_id} onChange={(e) => setManu(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                      <option value="">— Nessuno —</option>
                      {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div><Label>Garanzia (mesi)</Label><Input type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: +e.target.value })} /></div>
                  <div className="sm:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <Button variant="outline" onClick={() => setEditing(false)}>Annulla</Button>
                    <Button data-testid="detail-save-product" onClick={saveEdit} className="ama-btn-primary">Salva modifiche</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-sm text-[#0a0a2e] font-medium break-words">{v || "—"}</div>
    </div>
  );
}
